/*
 * Copyright (c) 2023 by N-Ach (Pvt) Ltd
 *
 * ╔═╗ ╔╗    ╔═══╗  ╔╗
 * ║║╚╗║║    ║╔═╗║  ║║
 * ║╔╗╚╝║    ║║ ║╠══╣╚═╗
 * ║║╚╗║║╔══╗║╚═╝║╔═╣╔╗║
 * ║║ ║║║╚══╝║╔═╗║╚═╣║║║
 * ╚╝ ╚═╝    ╚╝ ╚╩══╩╝╚╝
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information
 * of N-Ach (Pvt) Ltd. You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms
 * of the license agreement you entered into with N-Ach (Pvt) Ltd.
 *
 *
 * @Author Dilhari Jayathunga
 * @Date 2/8/2023
 */

import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useResetPasswordMutation,
  useSendResetPWEmailMutation,
  useValidateOTPMutation,
} from "../Store/Services/AuthService";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import styles from "./ForgotPassword.module.css";

const schema = yup.object().shape({
  userPsw: yup
    .string()
    .min(8, "The password should be between 8-12 characters.")
    .required("Please enter Password ")
    .max(12, "The password should be between 8-12 characters.")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
      "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
    ),
  confirmPsw: yup
    .string()
    .oneOf([yup.ref("userPsw"), null], "Passwords must match"),
});

const ResetPassword = () => {
  const [isValidOTP, setIsValidOTP] = useState(false);
  const [otp, setOtp] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { email } = useParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [sendOtp] = useValidateOTPMutation();
  const [restPassword] = useResetPasswordMutation();
  const [resendOtp] = useSendResetPWEmailMutation();

  const enterOtp = async () => {
    const data = {
      otp: otp,
      email: email,
    };

    try {
      const result = await sendOtp(data).unwrap();
      if (result === 1) {
        setIsValidOTP(true);
      } else if (result === 0 || result === -2) {
        setErrorMessage("resend");
      } else if (result === -1) {
        setErrorMessage("mismatch");
      } else if (result === -3) {
        setErrorMessage("error");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erorr, something went wrong!");
    }
  };

  const submit = async (data) => {
    const dataSet = {
      email: email,
      password: data.userPsw,
    };
    try {
      const result = await restPassword(dataSet).unwrap();
      navigate(`/`);
    } catch (err) {
      console.error(err);
      toast.error("Erorr, something went wrong!");
    }
  };

  const onResend = async (e) => {
    e.preventDefault();
    setOtp("");
    const data = {
      email: email,
    };
    try {
      const result = await resendOtp(data).unwrap();
      setErrorMessage("");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
          {!isValidOTP ? (
            <div className={styles.container}>
              <Paper className={styles.paper}>
                <Typography variant="h5" textAlign="center" fontWeight="bold">
                  Enter the OTP
                </Typography>
                <Typography textAlign="center" color="text.secondary" mt={1}>
                  Please enter the OTP recieved to your email address to reset
                  your passowrd.
                </Typography>
                <div>
                  <div className={styles.formItem}>
                    <label className={styles.formLabel}>OTP:</label>
                    <TextField
                      className={styles.formText}
                      placeholder="Enter OTP"
                      type="number"
                      size="small"
                      value={otp}
                      fullWidth
                      onChange={(e) => {
                        setOtp(e.target.value);
                        setErrorMessage("");
                      }}
                    />
                  </div>
                  {errorMessage === "resend" ? (
                    <Typography className={styles.validation}>
                      Your OTP is timed out Please{" "}
                      <a href="" onClick={onResend}>
                        Resend
                      </a>{" "}
                      OTP.
                    </Typography>
                  ) : errorMessage === "mismatch" ? (
                    <Typography className={styles.validation}>
                      OTP does not match.
                    </Typography>
                  ) : errorMessage === "error" ? (
                    <Typography className={styles.validation}>
                      Error! Something went wrong.
                    </Typography>
                  ) : null}
                  <div className={styles.formItem}>
                    <Button
                      disabled={!otp}
                      onClick={() => enterOtp()}
                      variant="contained"
                      fullWidth
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Paper>
            </div>
          ) : (
            <div className={styles.container}>
              <Paper className={styles.paper}>
                <Typography variant="h5" textAlign="center" fontWeight="bold">
                  Reset Password
                </Typography>
                <form onSubmit={handleSubmit(submit)}>
                  <div className={styles.formItem}>
                    <label className={styles.formLabel}>New Password:</label>
                    <TextField
                      {...register("userPsw")}
                      size="small"
                      fullWidth
                      className={styles.formText}
                      placeholder="Enter Password"
                      type={showPassword ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={!!errors.userPsw}
                      helperText={errors.userPsw?.message}
                    />
                  </div>
                  <div className={styles.formItem}>
                    <label className={styles.formLabel}>
                      Confirm Password:
                    </label>
                    <TextField
                      {...register("confirmPsw")}
                      size="small"
                      fullWidth
                      className={styles.formText}
                      placeholder="Confirm Password"
                      type={showPassword ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      error={!!errors.confirmPsw}
                      helperText={errors.confirmPsw?.message}
                    />
                  </div>
                  <div className={styles.formItem}>
                    <Button type="submit" variant="contained" fullWidth>
                      Reset Password
                    </Button>
                  </div>
                </form>
              </Paper>
            </div>
          )}
    </div>
  );
};

export default ResetPassword;
